/**
 * Business Development API - typed wrappers over /api/v1/admin/bd.
 * Every call resolves (never throws) to { success, data } or
 * { success: false, error }, so a screen can tell a failure from an empty list.
 */
import { apiRequest, apiUpload } from '../../../services/api';
import type { ContactDirectory } from '../../../components/facilityContacts/facilityContactsApi';

export type Stage = 'PROSPECT' | 'COLD' | 'INTERESTED' | 'BAA_SENT' | 'BAA_SIGNED' | 'ACTIVE' | 'DROPPED' | 'DO_NOT_CONTACT';
export type Interest = 'INTERESTED' | 'WANTS_TO_HEAR_MORE' | 'NOT_INTERESTED';
export type ServiceLine = 'PODIATRY' | 'AUDIOLOGY' | 'OPTOMETRY' | 'DENTISTRY';
export type ActivityKind = 'VISIT' | 'CALL' | 'EMAIL' | 'MEETING' | 'NOTE';

export type Result<T> = { success: true; data: T } | { success: false; error?: string; status?: number };

export interface Meta {
  me: { id: string };
  today: string;
  stages: { key: Stage; label: string; open: boolean }[];
  dispositions: string[];
  stage_from_disposition: Record<string, Stage>;
  activity_kinds: ActivityKind[];
  service_lines: ServiceLine[];
  interests: Interest[];
  reps: { id: string; name: string; email: string }[];
  regions: { region: string; facilities: number }[];
  card_reading: boolean;
}

export interface Account {
  facility_id: string;
  name: string;
  city: string | null;
  county: string | null;
  region: string | null;
  address: string | null;
  zip_code: string | null;
  facility_type: 'SNF' | 'ALF' | null;
  beds: number | null;
  licensed_beds: string | null;
  phone: string | null;
  email: string | null;
  corporation: string[] | null;
  credentialing_status: string | null;
  board_status: string | null;
  board_stage: Stage | null;
  stage: Stage;
  stage_source: 'EMERGE' | 'BOARD';
  baa_sent: boolean | null;
  baa_sent_date: string | null;
  baa_signed: boolean | null;
  baa_signed_date: string | null;
  provider_next_visit_date: string | null;
  last_visit_date: string | null;
  tm_next_follow_up_date: string | null;
  owner_user_id: string | null;
  owner_name: string | null;
  podiatry_interest: Interest | null;
  audiology_interest: Interest | null;
  optometry_interest: Interest | null;
  dentistry_interest: Interest | null;
  current_provider: string | null;
  /** HISTORY_ONLY (2026-10-05): the TM board's free-text contact notes, as earlier imports left them. Never edited; contacts live in the facility directory. */
  key_contacts: string | null;
  next_step: string | null;
  next_step_on: string | null;
  last_activity_at: string | null;
  last_activity_kind: string | null;
  last_disposition: string | null;
}

export interface Activity {
  id: string;
  facility_id: string;
  facility_name?: string;
  user_id: string;
  user_name?: string | null;
  kind: string;
  disposition: string | null;
  service_lines: ServiceLine[];
  note: string | null;
  calendar_event_id: string | null;
  stage_from: Stage | null;
  stage_to: Stage | null;
  occurred_at: string;
}

export interface Sale {
  id: string;
  facility_id: string;
  facility_name?: string;
  facility_city?: string | null;
  region?: string | null;
  user_id: string;
  user_name?: string | null;
  service_lines: ServiceLine[];
  signed_on: string;
  beds: number | null;
  monthly_value_cents: number | null;
  note: string | null;
  status: 'WON' | 'REVERSED';
  reversal_reason: string | null;
}

export interface Lead {
  id: string;
  first_name: string;
  last_name: string;
  title: string | null;
  company: string | null;
  email: string | null;
  phone: string | null;
  source: string | null;
  contact_type: string;
  city: string | null;
  state: string | null;
  notes: string | null;
  created_at: string;
  owner_user_id: string | null;
  owner_name: string | null;
  facility_id: string | null;
  facility_name: string | null;
  funnel_status: string | null;
  funnel_event: string | null;
}

export interface TodayEvent {
  id: string;
  title: string;
  starts_at: string;
  ends_at: string;
  status: string;
  location_type: string;
  venue_name: string | null;
  address: string | null;
  internal_notes: string | null;
  facility_id: string | null;
  facility_name: string | null;
  facility_city: string | null;
  facility_region: string | null;
  stage: Stage | null;
  next_step: string | null;
  next_step_on: string | null;
  logged: number;
}

export interface Today {
  date: string;
  events: TodayEvent[];
  follow_ups: Account[];
  note: string;
  note_updated_at: string | null;
  activities: Activity[];
}

export interface Summary {
  stages: Record<Stage, number>;
  open_pipeline: number;
  activity_7d: Partial<Record<ActivityKind, number>>;
  wins: { mtd: number; mtd_beds: number; mtd_value_cents: string | number; qtd: number; qtd_beds: number; qtd_value_cents: string | number; ytd: number };
  overdue_follow_ups: number;
}

export interface MapPoint {
  id: string;
  name: string;
  city: string | null;
  region: string | null;
  type: 'SNF' | 'ALF' | null;
  beds: number | null;
  stage: Stage;
  owner_user_id: string | null;
  owner_name: string | null;
  lat: number;
  lng: number;
}

export interface CardScan {
  id: string;
  ocr_status: 'READ' | 'OCR_UNAVAILABLE' | 'OCR_FAILED';
  reason: string | null;
  parsed: Partial<{
    first_name: string; last_name: string; credentials: string | null; title: string; company: string; company_hint: string;
    email: string; website: string; phone: string; mobile: string; fax: string; address: string; city: string; state: string; zip: string;
    unused: string[];
  }>;
  facility_suggestion: { id: string; name: string; city: string | null } | null;
}

export interface LeadInput {
  first_name: string;
  last_name?: string;
  title?: string;
  company?: string;
  email?: string;
  phone?: string;
  address?: string;
  city?: string;
  state?: string;
  zip?: string;
  notes?: string;
  facility_id?: string | null;
  owner_user_id?: string | null;
}

const qs = (params: Record<string, string | number | undefined | null>) => {
  const p = Object.entries(params).filter(([, v]) => v !== undefined && v !== null && v !== '');
  return p.length ? `?${p.map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(String(v))}`).join('&')}` : '';
};

const call = async <T,>(method: 'GET' | 'POST' | 'PUT' | 'PATCH', url: string, body?: unknown): Promise<Result<T>> =>
  (await apiRequest<T>(method, `/admin/bd${url}`, body)) as unknown as Result<T>;

export const bdApi = {
  meta: () => call<Meta>('GET', '/meta'),
  today: (date?: string) => call<Today>('GET', `/today${qs({ date })}`),
  saveDayNote: (date: string, note: string) => call<{ note: string; updated_at: string | null }>('PUT', `/day-notes/${date}`, { note }),
  summary: (owner = 'mine') => call<Summary>('GET', `/summary${qs({ owner })}`),
  accounts: (f: { owner?: string; region?: string; stage?: string; type?: string; q?: string; sort?: string; limit?: number; offset?: number }) =>
    call<{ total: number; limit: number; offset: number; rows: Account[] }>('GET', `/accounts${qs(f)}`),
  account: (id: string) => call<{
    account: Account; activities: Activity[]; sales: Sale[]; leads: Lead[]; visits: { id: string; title: string; starts_at: string; status: string; host_name: string | null }[];
    /** The building's contacts from eMerge's directory (call-sheet view); null with directory_error when it could not be read. */
    directory?: ContactDirectory | null; directory_error?: boolean;
  }>('GET', `/accounts/${id}`),
  updateAccount: (id: string, patch: Partial<Account> & { note?: string }) => call<Account>('PATCH', `/accounts/${id}`, patch),
  logActivity: (id: string, body: { kind: ActivityKind; disposition?: string | null; service_lines?: ServiceLine[]; note?: string; calendar_event_id?: string; next_step?: string; next_step_on?: string; occurred_at?: string; apply_stage?: boolean }) =>
    call<{ activity: Activity; stage_moved: boolean; stage: Stage }>('POST', `/accounts/${id}/activities`, body),
  assign: (body: { owner_user_id: string | null; facility_ids?: string[]; region?: string; type?: 'SNF' | 'ALF' | 'ALL'; only_unassigned?: boolean }) =>
    call<{ matched: number; changed: number }>('POST', '/accounts/assign', body),
  planVisit: (body: { facility_id: string; starts_at: string; ends_at: string; title?: string; notes?: string }) => call<{ id: string }>('POST', '/visits', body),
  linkEvent: (eventId: string, facilityId: string | null) => call<{ id: string }>('PATCH', `/events/${eventId}/facility`, { facility_id: facilityId }),
  map: (type = 'ALL') => call<{ points: MapPoint[]; not_located: number }>('GET', `/map${qs({ type })}`),
  sales: (f: { owner?: string; from?: string; to?: string }) => call<Sale[]>('GET', `/sales${qs(f)}`),
  recordSale: (body: { facility_id: string; service_lines: ServiceLine[]; signed_on: string; beds?: number | null; monthly_value?: number | null; note?: string; user_id?: string }) =>
    call<Sale>('POST', '/sales', body),
  reverseSale: (id: string, reason: string) => call<Sale>('POST', `/sales/${id}/reverse`, { reason }),
  leads: (f: { owner?: string; q?: string; source?: string; limit?: number; offset?: number }) => call<{ total: number; rows: Lead[] }>('GET', `/leads${qs(f)}`),
  addLead: (body: LeadInput) => call<{ id: string; existed: boolean }>('POST', '/leads', body),
  updateLead: (id: string, patch: { owner_user_id?: string | null; facility_id?: string | null }) => call<{ id: string }>('PATCH', `/leads/${id}`, patch),
  scanCard: async (file: File): Promise<Result<CardScan>> => {
    const form = new FormData();
    form.append('image', file);
    return (await apiUpload<CardScan>('/admin/bd/cards', form)) as unknown as Result<CardScan>;
  },
  saveCard: (id: string, body: LeadInput) => call<{ id: string; existed: boolean }>('POST', `/cards/${id}/save`, body),
};

// ---------------------------------------------------------------- words

export const STAGE_TONE: Record<Stage, { bg: string; fg: string; dot: string }> = {
  PROSPECT:       { bg: 'bg-slate-100',   fg: 'text-slate-700',   dot: '#64748b' },
  COLD:           { bg: 'bg-sky-100',     fg: 'text-sky-800',     dot: '#0284c7' },
  INTERESTED:     { bg: 'bg-amber-100',   fg: 'text-amber-800',   dot: '#d97706' },
  BAA_SENT:       { bg: 'bg-orange-100',  fg: 'text-orange-800',  dot: '#ea580c' },
  BAA_SIGNED:     { bg: 'bg-violet-100',  fg: 'text-violet-800',  dot: '#7c3aed' },
  ACTIVE:         { bg: 'bg-emerald-100', fg: 'text-emerald-800', dot: '#2B6E62' },
  DROPPED:        { bg: 'bg-rose-100',    fg: 'text-rose-800',    dot: '#be123c' },
  DO_NOT_CONTACT: { bg: 'bg-zinc-200',    fg: 'text-zinc-700',    dot: '#3f3f46' },
};

export const STAGE_LABEL: Record<Stage, string> = {
  PROSPECT: 'Prospect', COLD: 'Cold', INTERESTED: 'Interested', BAA_SENT: 'BAA sent',
  BAA_SIGNED: 'BAA signed', ACTIVE: 'Active', DROPPED: 'Dropped', DO_NOT_CONTACT: 'Do not contact',
};

export const words = (s: string | null | undefined) =>
  String(s || '').toLowerCase().replace(/_/g, ' ').replace(/^\w/, (c) => c.toUpperCase());

export const SERVICE_LABEL: Record<ServiceLine, string> = { PODIATRY: 'Podiatry', AUDIOLOGY: 'Audiology', OPTOMETRY: 'Optometry', DENTISTRY: 'Dentistry' };

export const fmtDate = (iso: string | null | undefined) => {
  if (!iso) return '—';
  // A DATE column arrives as 'YYYY-MM-DD' (or midnight UTC); read the calendar day, not the local instant.
  const d = String(iso).slice(0, 10).split('-').map(Number);
  if (d.length !== 3 || d.some(Number.isNaN)) return '—';
  return new Date(Date.UTC(d[0], d[1] - 1, d[2])).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' });
};

export const fmtTime = (iso: string) =>
  new Date(iso).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', timeZone: 'America/New_York' });

export const fmtMoney = (cents: string | number | null | undefined) =>
  cents == null ? '—' : new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(Number(cents) / 100);
